import React, { useState } from 'react'

import { Alert, Stack, TextField, Typography } from '@mui/material'
import { useCustomerFeaturesReset } from 'features/customers/hooks/useCustomerFeaturesReset'

import { NOTE_MAX, resetToPlanNote } from '@albacross/types'

import { ConfirmModal } from 'components/_base'

type CustomerFeaturesResetModalProps = {
  open: boolean
  overrides: number
  byPeople: number
  planName?: string
  onClose: (...args: any[]) => any
}

const summary = (overrides: number, byPeople: number) => {
  if (!overrides) return 'Nothing is stored on this customer, so their plan already decides everything.'

  const values =
    overrides === 1 ? 'the one value stored on this customer' : `all ${overrides} values stored on this customer`

  if (!byPeople) return `This removes ${values}. Their plan decides everything again.`

  if (byPeople === overrides) {
    const who = overrides === 1 ? 'which a person set' : 'all of them set by a person'

    return `This removes ${values}, ${who}. Their plan decides everything again.`
  }

  return `This removes ${values}, ${byPeople} of them set by a person. Their plan decides everything again.`
}

const CustomerFeaturesResetModal = ({
  onClose,
  open,
  overrides,
  byPeople,
  planName,
}: CustomerFeaturesResetModalProps) => {
  const { mutateAsync: resetFeatures, isPending } = useCustomerFeaturesReset()
  const [note, setNote] = useState('')

  const defaultNote = resetToPlanNote(planName)

  // The dialog stays mounted when it closes, so a reason typed for an abandoned reset would come
  // back on the next one and be recorded by accident.
  const handleClose = (...args: unknown[]) => {
    setNote('')
    onClose(...args)
  }

  const handleReset = async () => {
    await resetFeatures(note.trim() || undefined)
    handleClose()
  }

  return (
    <ConfirmModal
      content={
        <Stack spacing={1.5}>
          <Typography variant="body2">{summary(overrides, byPeople)}</Typography>

          {!!overrides && (
            <>
              <TextField
                fullWidth
                helperText={`Recorded against every feature this clears. Left empty: "${defaultNote}".`}
                label="Why (optional)"
                onChange={event => setNote(event.target.value)}
                placeholder={defaultNote}
                size="small"
                slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
                value={note}
              />

              <Alert severity="warning">
                There is no undo. The history of each feature is kept, so you will still see what was changed and by
                whom.
              </Alert>
            </>
          )}
        </Stack>
      }
      loading={isPending}
      onClose={handleClose}
      onConfirm={handleReset}
      open={open}
      title="Reset every feature to the plan?"
    />
  )
}

export default CustomerFeaturesResetModal
